import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Box, Button, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { ui, tone } from '../../../utils/uiTokens'
import { capitalize } from '../../../utils/functions'
import { dirSubjectsArgs, directorApi, DirSubjectRow } from '../../../features/director/directorApi'
import { HeadFilters, useHeadFilters, useHeadIndex } from '../../head/headData'
import { useDirectorSubject } from '../../head/headScope'
import { Kpi, PctPill, Pill, SortCell, bodyRow, btn, headRow, kpiRow, page, sub, tableWrap, toneOf } from '../ui'

type SortKey = 'studentsTotal' | 'studentsActive' | 'studentsLeft' | 'studentsReturned' | 'videoPercent' | 'testPercent' | 'ratingPercent'

const SORTS: { key: SortKey; id: string }[] = [
    { key: 'studentsTotal', id: 'colStudents' },
    { key: 'studentsActive', id: 'dirActive' },
    { key: 'studentsLeft', id: 'dirLeft' },
    { key: 'studentsReturned', id: 'dirReturned' },
    { key: 'videoPercent', id: 'dirVideo' },
    { key: 'testPercent', id: 'test' },
    { key: 'ratingPercent', id: 'rating' },
]

const sum = (rows: DirSubjectRow[], k: SortKey | 'mentorCount') => rows.reduce((s, r) => s + (r[k] || 0), 0)

const DirectorSubjectsPage = () => {
    const { formatMessage, locale } = useIntl()
    const f = useHeadFilters()
    const index = useHeadIndex()
    const { subjectId } = useDirectorSubject()
    const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'studentsTotal', dir: -1 })
    const [open, setOpen] = useState<number | null>(null)

    const { data = [], isFetching } = directorApi.useGetSubjectsQuery(dirSubjectsArgs(f))
    const rows = useMemo(
        () =>
            data
                .filter((r) => !subjectId || r.subjectId === subjectId)
                .slice()
                .sort((a, b) => ((a[sort.key] || 0) - (b[sort.key] || 0)) * sort.dir),
        [data, subjectId, sort],
    )
    const title = (r: { titleKz?: string; titleRu?: string }) => r[`title${capitalize(locale)}`] || r.titleRu
    const toggleSort = (key: SortKey) => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : -1 }))

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <H3>{formatMessage({ id: 'subjects' })}</H3>
                <Box sx={{ ...sub, margin: '6px 0 18px' }}>{formatMessage({ id: 'dirSubjectsSub' })}</Box>

                <HeadFilters filters={f} index={index} showGroup={false} />
                {isFetching && <LinearProgress sx={{ marginBottom: 2 }} />}

                <Box sx={kpiRow}>
                    <Kpi dark label={formatMessage({ id: 'subjects' })} value={rows.length} hint={formatMessage({ id: 'dirMentorsN' }, { n: sum(rows, 'mentorCount') })} />
                    <Kpi label={formatMessage({ id: 'dirStudentsTotal' })} value={sum(rows, 'studentsTotal')} />
                    <Kpi label={formatMessage({ id: 'dirActive' })} value={sum(rows, 'studentsActive')} color={tone.ok.text} />
                    <Kpi
                        label={formatMessage({ id: 'dirLeftReturned' })}
                        value={`${sum(rows, 'studentsLeft')} / ${sum(rows, 'studentsReturned')}`}
                        color={tone.bad.text}
                    />
                </Box>

                <Box sx={tableWrap}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={headRow}>
                            <TableRow>
                                <TableCell>{formatMessage({ id: 'dirSubjectHead' })}</TableCell>
                                <TableCell align="right">{formatMessage({ id: 'colMentor' })}</TableCell>
                                {SORTS.map((s) => (
                                    <SortCell
                                        key={s.key}
                                        label={formatMessage({ id: s.id })}
                                        active={sort.key === s.key}
                                        dir={sort.dir}
                                        onClick={() => toggleSort(s.key)}
                                    />
                                ))}
                                <TableCell align="right">{formatMessage({ id: 'dirDetails' })}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <React.Fragment key={r.subjectId}>
                                    <TableRow sx={bodyRow}>
                                        <TableCell>
                                            <b>{title(r)}</b>
                                            <Box sx={{ fontSize: '12px', color: ui.muted }}>
                                                {formatMessage({ id: 'dirHead' })}: {r.headNames?.join(', ') || '-'}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{r.mentorCount}</TableCell>
                                        <TableCell align="center"><b>{r.studentsTotal}</b></TableCell>
                                        <TableCell align="center" sx={{ color: `${tone.ok.text} !important`, fontWeight: 700 }}>{r.studentsActive}</TableCell>
                                        <TableCell align="center" sx={{ color: r.studentsLeft ? `${tone.bad.text} !important` : undefined, fontWeight: 700 }}>{r.studentsLeft}</TableCell>
                                        <TableCell align="center" sx={{ color: r.studentsReturned ? `${tone.bad.text} !important` : undefined, fontWeight: 700 }}>{r.studentsReturned}</TableCell>
                                        <TableCell align="center"><PctPill value={r.videoPercent} good={85} warn={65} /></TableCell>
                                        <TableCell align="center"><PctPill value={r.testPercent} good={85} warn={60} /></TableCell>
                                        <TableCell align="center"><Pill tone={toneOf(r.ratingPercent, 75, 50)}>{r.ratingPercent ?? 0}%</Pill></TableCell>
                                        <TableCell align="right">
                                            <Button size="small" variant="outlined" sx={btn} onClick={() => setOpen(open === r.subjectId ? null : r.subjectId)}>
                                                {formatMessage({ id: open === r.subjectId ? 'dirClose' : 'dirDetailsOpen' })}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    {open === r.subjectId && (
                                        <TableRow>
                                            <TableCell colSpan={10} sx={{ background: ui.surface, padding: '12px 14px' }}>
                                                <SubjectMentors row={r} courseId={f.courseId} monthNumber={f.monthNumber} weekNumber={f.weekNumber} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                            {!isFetching && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ color: ui.muted, padding: '28px' }}>
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Box>
        </Box>
    )
}

// «Детальдар»: менторы пәна по лекам
const SubjectMentors: React.FC<{ row: DirSubjectRow; courseId: number | null; monthNumber?: number; weekNumber?: number }> = ({
    row,
    courseId,
    monthNumber,
    weekNumber,
}) => {
    const { formatMessage, locale } = useIntl()
    const navigate = useNavigate()
    const index = useHeadIndex()
    const { setSubjectId } = useDirectorSubject()
    const { data = [], isFetching } = directorApi.useGetSubjectMentorsQuery({
        subjectId: row.subjectId,
        courseId: courseId || 0,
        monthNumber: monthNumber || 0,
        weekNumber: weekNumber || 0,
    })
    const title = (r: { titleKz?: string; titleRu?: string }) => r[`title${capitalize(locale)}`] || r.titleRu
    const lekOf = (m) => m[`courseTitle${capitalize(locale)}`] || m.courseTitleRu
    const groupsOf = (mentorId: number, cId: number) =>
        (index.mentors.find((m) => m.mentorId === mentorId)?.groupIds ?? []).filter((g) => index.groupCourse.get(g) === cId)

    const openStudents = (mentorId: number, cId: number) => {
        const g = groupsOf(mentorId, cId)[0]
        setSubjectId(row.subjectId)
        navigate(`/director/students?courseId=${cId}${g ? `&groupId=${g}` : ''}`)
    }

    return (
        <Box>
            <Box sx={{ ...sub, marginBottom: '8px' }}>
                <b>{title(row)}</b> · {formatMessage({ id: 'dirHead' })} <b>{row.headNames?.join(', ') || '-'}</b> · {formatMessage({ id: 'dirMentorsByLek' })}
            </Box>
            {isFetching && <LinearProgress />}
            <Box sx={{ ...tableWrap, background: ui.card }}>
                <Table sx={{ minWidth: 760 }}>
                    <TableHead sx={headRow}>
                        <TableRow>
                            <TableCell>{formatMessage({ id: 'dirLekGroup' })}</TableCell>
                            <TableCell>{formatMessage({ id: 'colMentor' })}</TableCell>
                            <TableCell align="center">{formatMessage({ id: 'colStudents' })}</TableCell>
                            <TableCell align="center">{formatMessage({ id: 'dirActive' })}</TableCell>
                            <TableCell align="center">{formatMessage({ id: 'dirLeft' })}</TableCell>
                            <TableCell align="center">{formatMessage({ id: 'dirReturned' })}</TableCell>
                            <TableCell align="center">{formatMessage({ id: 'dirVideo' })}</TableCell>
                            <TableCell align="center">{formatMessage({ id: 'test' })}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((m) => (
                            <TableRow key={`${m.mentorId}:${m.courseId}`} sx={bodyRow}>
                                <TableCell>
                                    <b>{lekOf(m)}</b>
                                    <Box sx={{ fontSize: '12px', color: ui.muted }}>
                                        {groupsOf(m.mentorId, m.courseId).map((g) => index.groupTitle.get(g)).join(', ')}
                                    </Box>
                                </TableCell>
                                <TableCell><b>{m.mentorName || `#${m.mentorId}`}</b></TableCell>
                                <TableCell align="center">{m.studentsTotal}</TableCell>
                                <TableCell align="center" sx={{ color: `${tone.ok.text} !important`, fontWeight: 700 }}>{m.studentsActive}</TableCell>
                                <TableCell align="center" sx={{ color: m.studentsLeft ? `${tone.bad.text} !important` : undefined }}>{m.studentsLeft}</TableCell>
                                <TableCell align="center" sx={{ color: m.studentsReturned ? `${tone.bad.text} !important` : undefined }}>{m.studentsReturned}</TableCell>
                                <TableCell align="center"><PctPill value={m.videoPercent} good={85} warn={65} /></TableCell>
                                <TableCell align="center"><PctPill value={m.testPercent} good={85} warn={60} /></TableCell>
                                <TableCell align="right">
                                    <Button size="small" variant="outlined" sx={btn} onClick={() => openStudents(m.mentorId, m.courseId)}>
                                        {formatMessage({ id: 'students' })} →
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!isFetching && data.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ color: ui.muted }}>{formatMessage({ id: 'noData' })}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
        </Box>
    )
}

export default DirectorSubjectsPage
